'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { useMonitoring } from '@kit/monitoring/hooks';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';

import type { Drug } from '../components/drug-search';
import type { InteractionCheckResult } from '../types';
import {
  type CheckOptions,
  InteractionCheckSession,
  type InteractionProgress,
  type SessionSnapshot,
} from './InteractionCheckSession';

export type { CheckOptions, InteractionProgress };

/**
 * React hook for checking drug interactions.
 *
 * This is a thin wrapper around `InteractionCheckSession` — all lifecycle
 * logic (Realtime subscription, retry, polling, timeout) lives in the
 * session class, keeping this hook focused on React state management.
 */
export function useInteractionCheck() {
  const supabase = useSupabase();
  const monitoring = useMonitoring();
  const { i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [results, setResults] = useState<InteractionCheckResult[] | null>(null);
  const [progress, setProgress] = useState<InteractionProgress | null>(null);
  const [hasTimedOut, setHasTimedOut] = useState(false);

  // Session instance — created once, stable across renders.
  // State setters are stable, so capturing them in the closure is safe.
  const sessionRef = useRef<InteractionCheckSession | null>(null);

  if (sessionRef.current === null) {
    sessionRef.current = new InteractionCheckSession(
      supabase,
      monitoring,
      (snapshot: SessionSnapshot) => {
        setIsLoading(snapshot.isLoading);
        setErrorKey(snapshot.errorKey);
        setResults(snapshot.results);
        setProgress(snapshot.progress);
        setHasTimedOut(snapshot.hasTimedOut);
      },
    );
  }

  // Forward the UI language to the session (sent as `x-lang` on API calls).
  useEffect(() => {
    sessionRef.current?.setLanguage(i18n.language);
  }, [i18n.language]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      sessionRef.current?.destroy();
    };
  }, []);

  const checkInteractions = useCallback(
    async (
      drugs: Drug[],
      options?: CheckOptions,
    ): Promise<InteractionCheckResult[]> => {
      return sessionRef.current!.check(drugs, options);
    },
    [],
  );

  const probeCache = useCallback(
    async (
      drugs: Drug[],
    ): Promise<{ results: InteractionCheckResult[]; allDone: boolean }> => {
      return sessionRef.current!.probeCache(drugs);
    },
    [],
  );

  const reset = useCallback(() => {
    sessionRef.current?.reset();
  }, []);

  const cancel = useCallback(() => {
    sessionRef.current?.cancel();
  }, []);

  return {
    checkInteractions,
    probeCache,
    isLoading,
    errorKey,
    results,
    progress,
    reset,
    cancel,
    hasTimedOut,
  };
}
